import { isNotificationCollapsible } from "src/utils/chatroom";

import { UnifizeChat } from "src/types";
import { Nullable } from "src/ts-types";

export interface GroupedChatMetadata {
  /**
   * Whether to render a date separator before this chat.
   */
  dateBreak?: boolean;
  /**
   * Collapsing state for notification stacks: "first", "stacked", or "none".
   */
  collapsedState?: "first" | "stacked" | "none";
}

/**
 * Compares two ISO-8601 timestamp strings and determines whether they fall on the
 * same calendar day in UTC.
 *
 * @param timestamp1 - First timestamp string to compare.
 * @param timestamp2 - Second timestamp string to compare.
 * @returns `true` if both timestamps represent the same UTC year, month, and day; otherwise `false`.
 */
export const isSameUTCDay = (timestamp1: string, timestamp2: string) => {
  const date1 = new Date(timestamp1);
  const date2 = new Date(timestamp2);

  return (
    date1.getUTCFullYear() === date2.getUTCFullYear() &&
    date1.getUTCMonth() === date2.getUTCMonth() &&
    date1.getUTCDate() === date2.getUTCDate()
  );
};

/**
 * Processes recent chats to add metadata for UI rendering, such as date separators
 * and collapsible notification stacks. This function is designed to be used to prepare
 * chat data for display in a recent chats list.
 *
 * The function determines two main pieces of metadata for each chat:
 * 1.  `dateBreak`: Whether to render a date separator before the chat. A date break
 *     is added for the first chat in the list and for any chat that starts a new day.
 *     To avoid breaking up collapsed stacks, date breaks within a stack are suppressed
 *     unless the `notificationsExpanded` flag is true.
 *
 * 2.  `collapsedState`: Manages the collapsing of consecutive collapsible notifications.
 *     - `first`: The first chat in a collapsible stack.
 *     - `stacked`: A subsequent chat in a collapsible stack.
 *     - `none`: The chat is not part of a collapsible stack.
 *
 * @param chats An array of chat objects, expected to be in the order
 *   of display (e.g., newest to oldest).
 * @param notificationsExpanded A flag to control the display of notification stacks.
 *   If `true`, notification stacks are treated as expanded, and date breaks will be
 *   inserted within them if a new day begins.
 * @returns A tuple containing:
 *   - `augmentedChats`: A new array of chat objects, where each object is a copy of the
 *     original chat object, augmented with `dateBreak` and `collapsedState` metadata.
 *   - `stackCounts`: An array of numbers with the same length as the input `chats` array.
 *     For each collapsible stack, the element at the stack's starting index holds the
 *     total count of items in that stack. All other elements in the array are 0. This
 *     can be used, for example, to display the number of collapsed notifications.
 */
export const groupRecentChats = (
  chats: Array<UnifizeChat>,
  notificationsExpanded: boolean,
): [Array<UnifizeChat & GroupedChatMetadata>, Array<number>] => {
  let isStartOfStack = false,
    stackCounts = Array.from({ length: chats.length }, () => 0),
    stackIndex = -1,
    lastDateBreak: Nullable<string> = null;
  return [
    chats.reduce(
      (acc, chat, index) => {
        let dateBreak = index === 0;
        let collapsedState: GroupedChatMetadata["collapsedState"] = "none";
        const { timestamp } = chat;
        const isNewDay =
          lastDateBreak && !isSameUTCDay(lastDateBreak, timestamp);
        const collapsible = isNotificationCollapsible(chat, index);

        if (!isStartOfStack && collapsible) {
          isStartOfStack = true;
          collapsedState = "first";
          stackIndex = index;
          stackCounts[index] = 1;
        } else if (isStartOfStack && collapsible) {
          collapsedState = "stacked";
          if (stackIndex >= 0) stackCounts[stackIndex]!++;
        } else {
          isStartOfStack = false;
          stackIndex = -1;
        }

        if (index > 0 && isNewDay && (!collapsible || notificationsExpanded)) {
          dateBreak = true;
        }
        lastDateBreak = dateBreak ? timestamp : lastDateBreak;

        acc.push({
          ...chat,
          dateBreak,
          collapsedState,
        });
        return acc;
      },
      [] as Array<UnifizeChat & GroupedChatMetadata>,
    ),
    stackCounts,
  ];
};
